import React, { useCallback, useEffect, useState } from 'react'; // Path to your custom hook

import { OptimizedImageProps } from './optimized-image.types';
import { generateURL } from './utils';

export const OptimizedImage = ({
  className,
  src,
  alt,
  aspectRatio,
  format,
  loading,
  quality,
  blur,
  fit,
  crop,
  breakpoints = {},
  defaultWidth,
  height,
  width,
  fixedWidth,
  fluid,
  dataTestid,
  style,
}: OptimizedImageProps) => {
  const generateImageUrl = useCallback(
    (definedWidth?: number) =>
      generateURL({
        alt,
        src,
        width: definedWidth ?? width,
        height,
        aspectRatio,
        format,
        quality,
        blur,
        fit,
        crop,
      }),
    [src, width, height, aspectRatio, format, quality, blur, fit, crop, alt]
  );

  const [imageURL, setImageURL] = useState<string | undefined>(() => {
    if (src) {
      const defaultURL = generateImageUrl();
      sessionStorage.setItem(src, defaultURL);
      return defaultURL;
    }
  });

  const getMediaQuery = (mediaWidth: number) => `(max-width: ${mediaWidth}px)`;

  const srcset = Object.keys(breakpoints)
    .map((key) => `${generateImageUrl(breakpoints[key])} ${breakpoints[key]}w`)
    .join(', ');

  const sizes = Object.keys(breakpoints)
    .map((key, idx) =>
      idx === Object.keys(breakpoints).length - 1
        ? `${breakpoints[key]}px`
        : `${getMediaQuery(breakpoints[key])} ${breakpoints[key]}px`
    )
    .join(', ');

  const imgStyle: React.CSSProperties = {
    ...(fluid ? { width: '100%', height: 'auto' } : fixedWidth ? { width: fixedWidth, height: 'auto' } : {}),
    ...style,
  };

  useEffect(() => {
    setImageURL(generateImageUrl());
  }, [imageURL, src, defaultWidth, width, height, aspectRatio, format, blur, quality, fit, crop, generateImageUrl]);

  if (!src)
    return (
      <img
        className={className}
        src={generateURL({
          alt,
          src,
          width,
          height,
          aspectRatio,
          format,
          quality,
          blur,
          fit,
          crop,
        })}
        alt='Cover event'
        data-testid='non-optimized-image'
      />
    );
  return src.includes('blob:') ? (
    <img className={className} src={src} alt='Cover event' data-testid='non-optimized-image' />
  ) : (
    <img
      className={className}
      data-testid={dataTestid}
      alt={alt}
      loading={loading ?? 'lazy'}
      style={imgStyle}
      srcSet={srcset}
      width={width}
      height={height}
      src={imageURL}
      sizes={sizes}
      onLoad={() => {
        // Once the image is loaded, we can clear the session storage cache.
        sessionStorage.removeItem(src);
      }}
    />
  );
};
